"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { claimDailyBonus } from "@/lib/tokens";
import { INTRO_SEEN_EVENT, getIntroSeenLocal } from "@/lib/introSeen";

const DAY_MS = 24 * 60 * 60 * 1000;
const DAYS = [1, 2, 3, 4, 5, 6, 7];
// Guests see the popup once per browser-tab session (signed-in players are
// gated by the real 24h availability instead).
const GUEST_SEEN_KEY = "hoav:dailyGuestSeen";

// The daily login reward screen: a 7-day calendar (Day N = N * 5 tokens, so
// Day 7 = 35). For signed-in players it pops up once per day with a Claim
// button (the claim_daily_bonus RPC advances the streak and surfaces any
// error). Guests see the same calendar locked, with a sign-in CTA instead —
// the rewards are the nudge to register. Rendered globally from the layout.
export default function DailyRewardPopup() {
  const { user, profile, loading, applyTokenBalance, refreshProfile } =
    useAuth();
  const [open, setOpen] = useState(false);
  // True once claimed or dismissed this session, so it won't nag on every
  // navigation (signed-in: can reappear after a full reload if still unclaimed;
  // guest: suppressed for the tab session via sessionStorage).
  const [handled, setHandled] = useState(false);
  const [status, setStatus] = useState<"idle" | "claiming" | "claimed" | "error">(
    "idle",
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [result, setResult] = useState<{ reward: number; streak: number } | null>(
    null,
  );
  // Reactive mirror of the intro-seen flag: seeded post-mount and flipped by the
  // INTRO_SEEN_EVENT when the player clicks "Begin", so the popup opens right
  // after the intro instead of only on the next refresh.
  const [introSeen, setIntroSeen] = useState(false);

  const isGuest = !loading && !user;

  useEffect(() => {
    setIntroSeen(getIntroSeenLocal());
    const onSeen = () => setIntroSeen(true);
    window.addEventListener(INTRO_SEEN_EVENT, onSeen);
    return () => window.removeEventListener(INTRO_SEEN_EVENT, onSeen);
  }, []);

  // Mirror the server's logic to preview whether a reward is available and which
  // day it is. The RPC is authoritative; this is just for display.
  const { available, claimDay } = useMemo(() => {
    if (!profile) return { available: false, claimDay: 1 };
    const last = profile.last_daily_claim
      ? new Date(profile.last_daily_claim).getTime()
      : null;
    const streak = profile.daily_streak ?? 0;
    const now = Date.now();
    const avail = last === null || now - last >= DAY_MS;
    let day: number;
    if (last === null || now - last > 2 * DAY_MS || streak >= 7) day = 1;
    else day = streak + 1;
    return { available: avail, claimDay: day };
  }, [profile]);

  useEffect(() => {
    if (loading || handled) return;
    // Don't fight the one-time intro screen on a brand-new visit; open as soon
    // as it's dismissed (introSeen flips via INTRO_SEEN_EVENT).
    if (!introSeen) return;
    if (user && profile) {
      if (available) setOpen(true);
    } else if (isGuest) {
      try {
        if (window.sessionStorage.getItem(GUEST_SEEN_KEY)) return;
      } catch {
        /* sessionStorage may be unavailable */
      }
      setOpen(true);
    }
  }, [loading, user, profile, available, handled, isGuest, introSeen]);

  if (!open) return null;

  const markSeen = () => {
    setHandled(true);
    if (isGuest) {
      try {
        window.sessionStorage.setItem(GUEST_SEEN_KEY, "1");
      } catch {
        /* ignore */
      }
    }
  };

  const close = () => {
    markSeen();
    setOpen(false);
  };

  const claim = async () => {
    setStatus("claiming");
    setErrorMsg("");
    try {
      const res = await claimDailyBonus();
      if (!res.claimed) {
        setHandled(true);
        setOpen(false);
        return;
      }
      applyTokenBalance(res.balance);
      setResult({ reward: res.reward, streak: res.streak });
      setStatus("claimed");
      setHandled(true);
      refreshProfile();
    } catch (e) {
      setStatus("error");
      setErrorMsg(
        e instanceof Error ? e.message : "Couldn't claim — try again.",
      );
    }
  };

  // For guests, no day is "today/claimable" — the whole week is locked.
  const claimedDay = result?.streak ?? claimDay;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-accent/40 bg-background p-6 text-center shadow-[0_0_50px_rgba(124,58,237,0.45)]">
        <h2 className="text-xl font-extrabold uppercase tracking-widest text-accent">
          Daily Reward
        </h2>
        <p className="mt-1 text-sm text-white/60">
          {isGuest
            ? "Sign in to claim daily login rewards — up to 35 tokens on Day 7."
            : "Log in each day — the streak climbs to 35 on Day 7."}
        </p>

        <div className="my-6 grid grid-cols-4 gap-2 sm:grid-cols-7">
          {DAYS.map((d) => {
            const reward = d * 5;
            const isClaimedNow = status === "claimed" && d === claimedDay;
            // Guests never have a claimable "today".
            const isToday = !isGuest && status !== "claimed" && d === claimDay;
            const isDone = isClaimedNow || (!isGuest && d < claimedDay);
            const cls = isToday
              ? "border-accent bg-accent/20 text-white ring-2 ring-accent"
              : isDone
                ? "border-green-400/50 bg-green-400/10 text-green-300"
                : "border-white/10 bg-white/5 text-white/40";
            return (
              <div
                key={d}
                className={`flex flex-col items-center gap-1 rounded-lg border px-1 py-2 ${cls}`}
              >
                <div className="text-[0.6rem] font-bold uppercase tracking-widest">
                  Day {d}
                </div>
                <div className="font-mono text-sm font-extrabold">{reward}</div>
                <div className="text-[0.7rem] leading-none">
                  {isDone ? "✓" : isGuest ? "🔒" : "🪙"}
                </div>
              </div>
            );
          })}
        </div>

        {isGuest ? (
          <>
            <p className="mb-4 text-sm text-white/70">
              Create a free account to start your streak and keep your tokens.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Link
                href="/login"
                onClick={markSeen}
                className="rounded-full bg-accent px-8 py-3 text-sm font-bold uppercase tracking-widest text-white transition hover:brightness-110"
              >
                Sign In to Claim
              </Link>
              <button
                onClick={close}
                className="rounded-full border border-white/20 px-8 py-3 text-sm font-bold uppercase tracking-widest text-white/70 transition hover:border-white/40"
              >
                Later
              </button>
            </div>
          </>
        ) : status === "claimed" && result ? (
          <>
            <div className="text-lg font-bold text-green-400">
              +{result.reward} tokens — Day {result.streak}!
            </div>
            <button
              onClick={close}
              className="mt-5 rounded-full bg-accent px-8 py-3 text-sm font-bold uppercase tracking-widest text-white transition hover:brightness-110"
            >
              Awesome
            </button>
          </>
        ) : (
          <>
            {status === "error" && (
              <div className="mb-3 text-sm text-red-400">{errorMsg}</div>
            )}
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              <button
                onClick={claim}
                disabled={status === "claiming"}
                className="rounded-full bg-accent px-8 py-3 text-sm font-bold uppercase tracking-widest text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {status === "claiming"
                  ? "Claiming…"
                  : `Claim ${claimDay * 5} tokens`}
              </button>
              <button
                onClick={close}
                className="rounded-full border border-white/20 px-8 py-3 text-sm font-bold uppercase tracking-widest text-white/70 transition hover:border-white/40"
              >
                Later
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
